/**
 * @file LegalPresetField.tsx
 * @description «Заготовка» нового текстового документа (phase10.5.X; phase19
 * черновики текстов; phase19.16 — адреса и названия из shared, SB140):
 * подставляет адрес, название и черновик текста. Непустой текст заменяется
 * только после подтверждения окном кабинета.
 */

import { useId, useState } from "react";

import { ConfirmDialog } from "../ui/ConfirmDialog";
import { Label } from "../ui/label";
import { SelectField } from "../ui/SelectField";
import { useConfirm } from "../ui/use-confirm";
import type { LegalDraftValues } from "../../hooks/legal/useLegalDraftForm";
import { LEGAL_TEXT_PRESETS } from "../../lib/legal/legal-presets";

interface Props {
  values: LegalDraftValues;
  onChange: (patch: Partial<LegalDraftValues>) => void;
}

const OPTIONS = LEGAL_TEXT_PRESETS.map((p) => ({ value: p.key, label: p.key === "custom" ? "Свой документ" : p.title }));

export function LegalPresetField({ values, onChange }: Props) {
  const [preset, setPreset] = useState("custom");
  const fieldId = useId();
  const { confirm, request, settle } = useConfirm();

  async function pick(key: string) {
    setPreset(key);
    const chosen = LEGAL_TEXT_PRESETS.find((p) => p.key === key);
    if (!chosen || chosen.key === "custom") return;
    const patch: Partial<LegalDraftValues> = { slug: chosen.slug, title: chosen.title };
    const hasText = values.contentHtml.trim().length > 0;
    if (chosen.draftHtml && (!hasText || (await confirm({ title: "Заменить текст документа?", description: "Текст заменится черновиком из заготовки.", action: "Заменить" })))) {
      patch.contentHtml = chosen.draftHtml;
    }
    onChange(patch);
  }

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={fieldId}>Заготовка</Label>
      <SelectField id={fieldId} ariaLabel="Заготовка документа" value={preset} onChange={(key) => void pick(key)} options={OPTIONS} />
      <p className="type-caption text-on-surface-variant">Подставит адрес, название и черновик текста. Текст обязательно вычитывает юрист.</p>
      <ConfirmDialog request={request} onSettle={settle} />
    </div>
  );
}
